import { Component, computed, effect, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { BsDaterangepickerConfig } from 'ngx-bootstrap/datepicker';
import { interval, map } from 'rxjs';
import { ButtonComponent } from '@bn/web-ui/button';
import { BsCustomDates, DatePickerComponent } from '@bn/web-ui/datepicker';
import { FilterChipsComponent } from '@bn/web-ui/filter';
import { ChipFilter, FilterItemChips } from '@bn/web-ui/filter/models';
import { InputComponent } from '@bn/web-ui/input/input.component';
import { EventLogsGroupByComponent } from '../event-logs-group-by/event-logs-group-by.component';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const MAX_WINDOW_HOURS = 72;

export interface EventLogsDateRange {
  from: number;
  to: number;
}

@Component({
  selector: 'app-app-shield-event-logs-toolbar',
  templateUrl: './event-logs-toolbar.component.html',
  styleUrls: ['./event-logs-toolbar.component.scss'],
  imports: [ButtonComponent, DatePickerComponent, EventLogsGroupByComponent, FaIconComponent, FilterChipsComponent, FormsModule, InputComponent],
})
export class EventLogsToolbarComponent {
  readonly from = input<number | undefined>();
  readonly to = input<number | undefined>();
  readonly query = input('');
  readonly isExporting = input(false);
  readonly isLoading = input(false);
  readonly hasLogs = input(false);
  readonly chipsConfig = input<FilterItemChips>({ dimensions: [] });
  readonly chipsValue = input<ChipFilter[]>([]);
  readonly groupByDims = input<string[]>([]);

  readonly dateRangeChange = output<EventLogsDateRange>();
  readonly queryChange = output<string>();
  readonly exportClicked = output<void>();
  readonly refreshClicked = output<void>();
  readonly filtersChange = output<ChipFilter[]>();
  readonly groupByChange = output<string[]>();

  readonly queryValue = signal('');

  readonly #now = toSignal(interval(60_000).pipe(map(() => Date.now())), { initialValue: Date.now() });

  readonly datePickerRange = computed<Date[]>(() => {
    const now = this.#now();

    return [new Date(this.from() ?? now - DAY_MS), new Date(this.to() ?? now)];
  });

  readonly datePickerConfig = computed<Partial<BsDaterangepickerConfig>>(() => {
    this.from();
    this.to();
    const now = this.#now();

    return {
      minDate: new Date(now - MAX_WINDOW_HOURS * HOUR_MS),
      maxDate: new Date(now),
      maxDateRange: 3,
      ranges: this.buildPresets(now),
    };
  });

  constructor() {
    effect(() => {
      if (this.query() === '') this.queryValue.set('');
    });
  }

  onRangeChange(value: string | string[]): void {
    const range = Array.isArray(value) ? value : [value];

    if (range.length < 2) return;

    const from = new Date(range[0]).getTime();
    const toDate = new Date(range[1]);

    if (Number.isNaN(from) || Number.isNaN(toDate.getTime())) return;

    const to = this.isMidnight(toDate) ? toDate.setHours(23, 59, 59, 999) : toDate.getTime();

    this.dateRangeChange.emit({ from, to });
  }

  onQueryChange(value: string): void {
    this.queryValue.set(value);
    this.queryChange.emit(value);
  }

  private isMidnight(date: Date): boolean {
    return date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0;
  }

  private buildPresets(now: number): BsCustomDates[] {
    return [
      { label: 'Last 24 hours', value: [new Date(now - DAY_MS), new Date(now)] },
      { label: 'Last 2 days', value: [new Date(now - 2 * DAY_MS), new Date(now)] },
      { label: 'Last 3 days', value: [new Date(now - MAX_WINDOW_HOURS * HOUR_MS), new Date(now)] },
    ];
  }
}
